import { useLayoutEffect, useRef, forwardRef, useImperativeHandle, type TextareaHTMLAttributes } from "react";

interface AutoGrowTextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "rows"> {
  /** Maximum number of lines before the textarea stops growing and starts scrolling. */
  maxLines?: number;
}

/**
 * Textarea that grows to fit its content up to maxLines, then scrolls.
 * Used for places (like the topic name in the blueprint table) where the
 * input is conceptually single-line but long strings need to wrap rather
 * than truncate.
 */
export const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(
  function AutoGrowTextarea({ maxLines = 3, value, style, className, ...rest }, ref) {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    // Grow the textarea to fit its content. CSS max-height (set below)
    // caps it at maxLines, with overflow-y:auto kicking in past that.
    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      const fit = () => {
        el.style.height = "auto"; // reset so shrinking also works
        el.style.height = el.scrollHeight + "px";
      };
      fit();

      // Re-fit when the textarea's width changes (window/container resize):
      // a narrower width wraps the text onto more lines, so a height computed
      // at a wider width would leave the extra lines hidden behind a scroll.
      // Guard on width so our own height changes don't feed back into a loop.
      let lastWidth = el.clientWidth;
      const observer = new ResizeObserver(() => {
        if (el.clientWidth !== lastWidth) {
          lastWidth = el.clientWidth;
          fit();
        }
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, [value]);

    // Set max-height from computed line-height + padding once mounted (and
    // when maxLines changes), so the cap matches the actual rendered text.
    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      const cs = getComputedStyle(el);
      const lineHeightPx = parseFloat(cs.lineHeight);
      const lh = Number.isFinite(lineHeightPx) ? lineHeightPx : 24;
      const padTop = parseFloat(cs.paddingTop) || 0;
      const padBottom = parseFloat(cs.paddingBottom) || 0;
      el.style.maxHeight = lh * maxLines + padTop + padBottom + "px";
    }, [maxLines, className]);

    return (
      <textarea
        ref={innerRef}
        rows={1}
        value={value}
        className={className}
        style={{ resize: "none", overflowY: "auto", ...style }}
        {...rest}
      />
    );
  }
);
